import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef} from '@angular/material';
import {FormGroup} from '@angular/forms';
import {FieldType} from '../../../models/field-type';
import {Validator} from '../../../models/validator.enum';
import {FormConfig} from '../../../models/form-config';
import {FormFactoryService} from '../../../services/form-factory.service';
import {CalculationCacheService} from '../../../../core/services/impl/calculation-cache.service';
import {DialogConfigBase} from '../dialog-config-base';
import {DialogConfigInterface} from '../dialog-config.interface';
import {CalculationCacheModel} from '../../../../core/models/calculation-cache.model';
import {SessionService} from '../../../../core/services/impl/session/session.service';
import {SectionModeState, SectionsStateService} from '../../../services/sections-state.service';
import {
  VehicleModelStateService
} from '../../../../core/services/impl/section-services/vehicle-model/vehicle-model-state.service';
import {isKIOSKPOLIS, isMobile, isNestBank} from '../../../../core/utils/utils';
import {ApplicationStateService} from '../../../services/application-state.service';
import {SectionComponentKey} from '../../../../core/enums/components-keys';
import {FieldCaptionTarget} from '../../../models/field-config';
import {RegExpKey, RegExpService} from '../../../services/reg-exp.service';
import {AssetsService} from '../../../services/assets.service';

@Component({
  selector: 'app-calculation-cache-verification-dialog',
  templateUrl: './calculation-cache-verification-dialog.component.html',
  styleUrls: ['./calculation-cache-verification-dialog.component.scss']
})
export class CalculationCacheVerificationDialogComponent extends DialogConfigBase implements OnInit {

  public readonly CALCULATION_CACHE_VERIFICATION_DIALOG = 'CALCULATION_CACHE_VERIFICATION_DIALOG';
  public readonly POSTAL_CODE_CONTROL_KEY = 'postalCode';
  public readonly MAX_VERIFICATION_ATTEMPT = 3;

  public bean: any = {};
  public formConfig: FormConfig;
  public formGroup: FormGroup = new FormGroup({});
  public formFactoryService: FormFactoryService;
  public verificationValid: boolean = true;
  public verificationAttempt: number = 1;

  static getConfigBuilder(): any {
    return new CalculationCacheVerificationDialogConfig();
  }

  get nextCalculationsCacheVerificationDialog(): boolean {
    return isKIOSKPOLIS() || isNestBank();
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: CalculationCacheVerificationDialogData,
              private stateService: ApplicationStateService,
              private dialog: MatDialogRef<CalculationCacheVerificationDialogComponent>,
              private calculationCacheService: CalculationCacheService,
              private sessionService: SessionService,
              private vehicleModelStateService: VehicleModelStateService,
              private sectionStateService: SectionsStateService,
              private regExpService: RegExpService,
              private assetsService: AssetsService) {
    super();
  }

  ngOnInit() {
    this.createFormConfig();
    this.buildForm();
    this.valueChanges();
  }

  submitForm(): void {
    this.formFactoryService.submitForm();

    if (this.formFactoryService.isFormValid()) {
      this.verificationAttempt++;

      const postalCodeValue = this.formGroup.get(this.POSTAL_CODE_CONTROL_KEY).value;

      this.calculationCacheService.validateCalculation(postalCodeValue, this.data.calculation.pcId).then(valid => {
        this.verificationValid = valid;

        if (valid) {
          this.stateService.state.dataFromCalculationCache = true;
          this.stateService.dataLoadedFromCalculationsCache = true;
          this.sessionService.getSessionById(this.data.calculation.sessionId).then(() => {
            this.vehicleModelStateService.init();
            if (!isMobile()) {
              this.sectionStateService.setModeInAllSections(SectionModeState.EDIT);
            }
            this.sectionStateService.resetCreatedAtLeastOnce(SectionComponentKey.VEHICLE_MODEL);
            this.close(true);
          })
        } else {
          if (this.verificationAttempt > this.MAX_VERIFICATION_ATTEMPT) {
            this.calculationCacheService.removeCalculationById(this.data.calculation.sessionId);
            this.close();
          }
        }
      });
    }
  }

  close(param?: boolean): void {
    this.dialog.close(param);
  }

  private buildForm(): void {
    this.formFactoryService = new FormFactoryService(this.formConfig, this.bean);
    this.formFactoryService.buildFormGroup(this.formGroup);
  }

  private createFormConfig(): void {
    this.formConfig = {
      formKey: this.CALCULATION_CACHE_VERIFICATION_DIALOG,
      fieldsConfig: [{
        fieldKey: this.POSTAL_CODE_CONTROL_KEY,
        caption: this.CALCULATION_CACHE_VERIFICATION_DIALOG + '.postalCodeLabel',
        captionTarget: FieldCaptionTarget.PLACEHOLDER,
        type: FieldType.TextField,
        displayInPreviewMode: true,
        validators: [
          Validator.REQUIRED_NOT_BLANK,
          Validator.POSTAL_CODE,
          {
            name: Validator.PATTERN,
            pattern: this.regExpService.getRegExp(RegExpKey.POSTAL_CODE)
          }],
        maskConfig: {
          mask: '00-000',
          dropSpecialCharacters: false
        },
        autocomplete: 'postal-code',
        properties: {
          mobileInputType: 'tel',
        },
        appearance: this.nextCalculationsCacheVerificationDialog ? 'outline' : 'legacy',
        prefixIcon: this.nextCalculationsCacheVerificationDialog ? `${this.assetsService.getIconsDirectoryPath()}/mailbox.svg` : null,
      }]
    };
  }

  private valueChanges() {
    this.formGroup.valueChanges.subscribe(() => this.verificationValid = true);
  }
}

export class CalculationCacheVerificationDialogConfig extends DialogConfigBase implements DialogConfigInterface {
  private config: MatDialogConfig = {
    panelClass: 'app-dialog',
    disableClose: true
  };

  constructor() {
    super();
  }

  buildConfiguration(data: CalculationCacheVerificationDialogData): MatDialogConfig {
    return this.buildConfig(data, this.config);
  }
}

export interface CalculationCacheVerificationDialogData {
  calculation: CalculationCacheModel;
}
